import { useEffect, useState } from "react";
import { useSettings } from "../contexts/SettingsContext";
import {
  BACKGROUND_TASK_EVENT,
  type BackgroundTaskEventDetail,
} from "../lib/background-task-notifications";
import {
  AGENT_TASK_NOTIFICATION_PROMPT,
  agentTaskNotificationEnabled,
  hasVisibleAgentChatSurface,
  isAppOutOfView,
  markAgentTaskNotificationsEnabled,
  shouldShowAgentTaskNotificationPrompt,
  snoozeAgentTaskNotificationPrompt,
} from "../lib/agent-task-notification-policy";

type ElectronNotificationBridge = {
  electron?: {
    systemNotification?: {
      show?: (title: string, body?: string) => Promise<unknown>;
    };
  };
};

export function BackgroundTaskNotificationBridge() {
  const { preferences, updatePreferences } = useSettings();
  const [permissionPromptOpen, setPermissionPromptOpen] = useState(false);
  const systemDeliveryEnabled =
    preferences.notifications.deliveryChannels.desktop ||
    preferences.notifications.deliveryChannels.web;

  useEffect(() => {
    const onTask = (event: Event) => {
      const detail = (event as CustomEvent<BackgroundTaskEventDetail>).detail;
      if (!detail || detail.status === "started") return;

      const title =
        detail.title ||
        (detail.source === "narrativeflow" ? "NarrativeFlow" : "CAO Chat");
      const description = detail.detail;

      if (isAppOutOfView()) {
        if (systemDeliveryEnabled && agentTaskNotificationEnabled()) {
          void showSystemNotification(title, description);
        }
        return;
      }

      if (hasVisibleAgentChatSurface()) return;
      if (shouldShowAgentTaskNotificationPrompt()) {
        setPermissionPromptOpen(true);
      }
    };

    window.addEventListener(BACKGROUND_TASK_EVENT, onTask);
    return () => window.removeEventListener(BACKGROUND_TASK_EVENT, onTask);
  }, [systemDeliveryEnabled]);

  const enableNotifications = async () => {
    const permission = await requestBrowserNotificationPermission();
    if (permission === "denied") {
      snoozeAgentTaskNotificationPrompt();
      setPermissionPromptOpen(false);
      return;
    }

    markAgentTaskNotificationsEnabled();
    updatePreferences({
      notifications: {
        ...preferences.notifications,
        deliveryChannels: {
          ...preferences.notifications.deliveryChannels,
          desktop: true,
          web: true,
        },
      },
    });
    setPermissionPromptOpen(false);
  };

  const chooseLater = () => {
    snoozeAgentTaskNotificationPrompt();
    setPermissionPromptOpen(false);
  };

  return (
    <AgentTaskNotificationPermissionPrompt
      open={permissionPromptOpen}
      onEnable={enableNotifications}
      onLater={chooseLater}
    />
  );
}

async function showSystemNotification(title: string, body?: string) {
  const bridge = (window as unknown as ElectronNotificationBridge).electron
    ?.systemNotification?.show;
  if (!bridge) {
    if (
      "Notification" in window &&
      window.Notification.permission === "granted"
    ) {
      try {
        new window.Notification(title, body ? { body } : undefined);
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }
  try {
    await bridge(title, body);
    return true;
  } catch {
    return false;
  }
}

async function requestBrowserNotificationPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "granted";
  }

  if (window.Notification.permission === "default") {
    try {
      return await window.Notification.requestPermission();
    } catch {
      return "denied";
    }
  }

  return window.Notification.permission;
}

function AgentTaskNotificationPermissionPrompt({
  open,
  onEnable,
  onLater,
}: {
  open: boolean;
  onEnable: () => void;
  onLater: () => void;
}) {
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!open) {
      setEntered(false);
      const timer = window.setTimeout(() => setMounted(false), 180);
      return () => window.clearTimeout(timer);
    }

    setMounted(true);
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  if (!mounted) return null;

  return (
    <div
      className="fintheon-agent-notification-permission"
      data-open={entered ? "true" : "false"}
      role="dialog"
      aria-live="polite"
      aria-label="Agent task notifications"
    >
      <div className="fintheon-agent-notification-permission__surface">
        <p className="fintheon-agent-notification-permission__copy">
          {AGENT_TASK_NOTIFICATION_PROMPT}
        </p>
        <div className="fintheon-agent-notification-permission__actions">
          <button
            type="button"
            className="fintheon-agent-notification-permission__later"
            onClick={onLater}
          >
            Later
          </button>
          <button
            type="button"
            className="fintheon-agent-notification-permission__enable"
            onClick={onEnable}
          >
            Enable Notifications
          </button>
        </div>
      </div>
    </div>
  );
}
